import React, { useMemo } from 'react';
import { Alert, Radio, Space, Spin, Typography } from 'antd';
import type { PhysicalCargoSourceRow } from '../../types/physicalCargoSource';
import { usePhysicalCargoSourcesQuery } from './usePhysicalCargoSourcesQuery';
import {
  filterSelectablePhysicalSources,
  formatPhysicalSourceOptionDescription,
  loadingPointToPhysicalSourceKey,
  physicalSourceSelectionKey,
  physicalSourceToLoadingPoint,
  type PhysicalSourceSelectionKey
} from './physicalSourceLoadingPoint';

export type PhysicalCargoSourcePickerProps = {
  applicationId: number | null | undefined;
  loadingPointType?: 'application' | 'warehouse' | string | null;
  loadingPointId?: number | null;
  excludeRouteId?: number;
  disabled?: boolean;
  skip?: boolean;
  onSourceSelected: (patch: ReturnType<typeof physicalSourceToLoadingPoint>) => void;
};

export const PhysicalCargoSourcePicker: React.FC<PhysicalCargoSourcePickerProps> = ({
  applicationId,
  loadingPointType,
  loadingPointId,
  excludeRouteId,
  disabled,
  skip,
  onSourceSelected
}) => {
  const appId = applicationId != null ? Number(applicationId) : NaN;
  const { data, isFetching, isLoading, isError } = usePhysicalCargoSourcesQuery({
    applicationId: appId,
    excludeRouteId,
    skip: skip || !Number.isFinite(appId) || appId <= 0
  });

  const sources = useMemo(
    () => filterSelectablePhysicalSources(data?.sources ?? []),
    [data?.sources]
  );

  const selectedKey: PhysicalSourceSelectionKey | null = loadingPointToPhysicalSourceKey({
    applicationId: appId,
    loadingPointType,
    loadingPointId
  });

  if (!Number.isFinite(appId) || appId <= 0) {
    return (
      <Typography.Text type="secondary">Сначала выберите заявку (шаг 1).</Typography.Text>
    );
  }

  if (isError) {
    return <Alert type="error" showIcon message="Не удалось загрузить физические источники груза" />;
  }

  if (isLoading || isFetching) {
    return <Spin size="small" />;
  }

  if (sources.length === 0) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Для выбранной заявки нет физических источников с доступным остатком (CP-059)."
      />
    );
  }

  return (
    <Radio.Group
      disabled={disabled}
      value={selectedKey ?? undefined}
      onChange={(e) => {
        const key = String(e.target.value) as PhysicalSourceSelectionKey;
        const source = sources.find((s) => physicalSourceSelectionKey(s) === key);
        if (!source) return;
        onSourceSelected(physicalSourceToLoadingPoint(source, appId));
      }}
    >
      <Space direction="vertical">
        {sources.map((source) => {
          const key = physicalSourceSelectionKey(source);
          return (
            <Radio key={key} value={key}>
              <span>{source.label}</span>
              <Typography.Text type="secondary">
                {' '}
                — {formatPhysicalSourceOptionDescription(source)}
              </Typography.Text>
            </Radio>
          );
        })}
      </Space>
    </Radio.Group>
  );
};

/** Test helper — aggregate sources for assertions without UI. */
export function summarizePhysicalSourcesForTests(sources: PhysicalCargoSourceRow[]) {
  return filterSelectablePhysicalSources(sources).map((s) => ({
    key: physicalSourceSelectionKey(s),
    cargoCount: s.cargoCount,
    availableQuantitySum: s.availableQuantitySum
  }));
}
